import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../../components/ui/Header';
import Button from '../../../components/ui/Button';
import EmployeeModal from './components/EmployeeModal';
import EmployeeDetailsModal from './components/EmployeeDetailsModal';
import PayrollModal from './components/PayrollModal';
import PayrollDetailsModal from './components/PayrollDetailsModal';
import AttendanceModal from './components/AttendanceModal';
import AttendanceDetailsModal from './components/AttendanceDetailsModal';
import { API_ENDPOINTS } from '../../../utils/config';
import { 
  Users, 
  DollarSign, 
  Calendar, 
  FileText, 
  Plus, 
  Search, 
  Filter,
  Edit,
  Trash2,
  Eye,
  Download,
  Upload,
  Clock,
  Building,
  Mail,
  Phone,
  MapPin,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  Clock as ClockIcon
} from 'lucide-react';

const PayrollHR = () => {
  const [activeTab, setActiveTab] = useState('employees');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Employee state
  const [employees, setEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeModalMode, setEmployeeModalMode] = useState('add');
  const [isEmployeeDetailsModalOpen, setIsEmployeeDetailsModalOpen] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [employeeDetailsError, setEmployeeDetailsError] = useState('');
  const [isEmployeeDetailsLoading, setIsEmployeeDetailsLoading] = useState(false);
  const [viewingEmployeeId, setViewingEmployeeId] = useState(null);
  const [deletingEmployeeId, setDeletingEmployeeId] = useState(null);
  const [prefetchingEmployeeId, setPrefetchingEmployeeId] = useState(null);
  const [employeePendingDelete, setEmployeePendingDelete] = useState(null);
  
  const [notification, setNotification] = useState(null);
  
  // Payroll state
  const [payrolls, setPayrolls] = useState([]);
  const [isLoadingPayrolls, setIsLoadingPayrolls] = useState(false);
  const [isPayrollModalOpen, setIsPayrollModalOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [payrollModalMode, setPayrollModalMode] = useState('add');
  const [isPayrollDetailsModalOpen, setIsPayrollDetailsModalOpen] = useState(false);
  const [payrollDetails, setPayrollDetails] = useState(null);
  const [payrollDetailsError, setPayrollDetailsError] = useState('');
  const [isPayrollDetailsLoading, setIsPayrollDetailsLoading] = useState(false);
  const [viewingPayrollId, setViewingPayrollId] = useState(null);
  const [deletingPayrollId, setDeletingPayrollId] = useState(null);
  const [prefetchingPayrollId, setPrefetchingPayrollId] = useState(null);
  const [payrollPendingDelete, setPayrollPendingDelete] = useState(null);
  const [payrollSearchTerm, setPayrollSearchTerm] = useState('');
  
  // Attendance state
  const [attendances, setAttendances] = useState([]);
  const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [attendanceModalMode, setAttendanceModalMode] = useState('add');
  const [isAttendanceDetailsModalOpen, setIsAttendanceDetailsModalOpen] = useState(false);
  const [attendanceDetails, setAttendanceDetails] = useState(null);
  const [attendanceDetailsError, setAttendanceDetailsError] = useState('');
  const [isAttendanceDetailsLoading, setIsAttendanceDetailsLoading] = useState(false);
  const [viewingAttendanceId, setViewingAttendanceId] = useState(null);
  const [deletingAttendanceId, setDeletingAttendanceId] = useState(null);
  const [prefetchingAttendanceId, setPrefetchingAttendanceId] = useState(null);
  const [attendancePendingDelete, setAttendancePendingDelete] = useState(null);
  const [attendanceSearchTerm, setAttendanceSearchTerm] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    const parsedDate = new Date(dateString);
    return Number.isNaN(parsedDate.getTime()) ? 'N/A' : parsedDate.toLocaleDateString();
  };

  const fetchEmployees = async () => {
    setIsLoadingEmployees(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.EMPLOYEES_LIST, { headers });
      
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (Array.isArray(response.data.results)) {
        items = response.data.results;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      }
      
      setEmployees(items);
    } catch (error) {
      setEmployees([]);
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Unable to load employees.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'employees') {
      fetchEmployees();
    } else if (activeTab === 'payroll') {
      fetchPayrolls();
    } else if (activeTab === 'time-tracking') {
      fetchAttendances();
    }
  }, [activeTab]);

  useEffect(() => {
    if (notification?.message) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const loadEmployeeDetails = async (employeeId) => {
    const headers = getAuthHeaders();
    const response = await axios.get(API_ENDPOINTS.EMPLOYEES_DETAIL(employeeId), { headers });
    return response.data;
  };

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setEmployeeModalMode('add');
    setIsEmployeeModalOpen(true);
  };

  const handleEditEmployee = async (employee) => {
    if (!employee?.id) return;
    setPrefetchingEmployeeId(employee.id);
    try {
      const detailedEmployee = await loadEmployeeDetails(employee.id);
      setSelectedEmployee(detailedEmployee);
      setEmployeeModalMode('edit');
      setIsEmployeeModalOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 'Unable to load employee details for editing.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setPrefetchingEmployeeId(null);
    }
  };

  const handleEmployeeSave = () => {
    fetchEmployees();
    setNotification({
      type: 'success',
      message: employeeModalMode === 'edit' ? 'Employee updated successfully.' : 'Employee created successfully.'
    });
  };

  const handleCloseEmployeeModal = () => {
    setIsEmployeeModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleViewEmployee = async (employeeId) => {
    setViewingEmployeeId(employeeId);
    setIsEmployeeDetailsModalOpen(true);
    setIsEmployeeDetailsLoading(true);
    setEmployeeDetailsError('');
    setEmployeeDetails(null);
    try {
      const detail = await loadEmployeeDetails(employeeId);
      setEmployeeDetails(detail);
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to fetch employee details.';
      setEmployeeDetailsError(apiError);
    } finally {
      setIsEmployeeDetailsLoading(false);
      setViewingEmployeeId(null);
    }
  };

  const handleCloseEmployeeDetails = () => {
    setIsEmployeeDetailsModalOpen(false);
    setEmployeeDetails(null);
    setEmployeeDetailsError('');
  };

  const handleDeleteEmployeeRequest = (employee) => {
    if (!employee?.id) return;
    setEmployeePendingDelete(employee);
  };

  const handleDeleteEmployeeConfirm = async () => {
    const employeeId = employeePendingDelete?.id;
    if (!employeeId) return;

    setDeletingEmployeeId(employeeId);
    try {
      await axios.delete(API_ENDPOINTS.EMPLOYEES_DELETE(employeeId), {
        headers: getAuthHeaders()
      });
      setNotification({ type: 'success', message: 'Employee deleted successfully.' });
      await fetchEmployees();
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Failed to delete employee.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setDeletingEmployeeId(null);
      setEmployeePendingDelete(null);
    }
  };

  // Payroll functions
  const fetchPayrolls = async () => {
    setIsLoadingPayrolls(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.PAYROLL_LIST, { headers });
      
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (Array.isArray(response.data.results)) {
        items = response.data.results;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      }
      
      setPayrolls(items);
    } catch (error) {
      setPayrolls([]);
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Unable to load payrolls.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setIsLoadingPayrolls(false);
    }
  };

  const loadPayrollDetails = async (payrollId) => {
    const headers = getAuthHeaders();
    const response = await axios.get(API_ENDPOINTS.PAYROLL_DETAIL(payrollId), { headers });
    return response.data;
  };

  const handleAddPayroll = () => {
    setSelectedPayroll(null);
    setPayrollModalMode('add');
    setIsPayrollModalOpen(true);
  };

  const handleEditPayroll = async (payroll) => {
    if (!payroll?.id) return;
    setPrefetchingPayrollId(payroll.id);
    try {
      const detailedPayroll = await loadPayrollDetails(payroll.id);
      setSelectedPayroll(detailedPayroll);
      setPayrollModalMode('edit');
      setIsPayrollModalOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 'Unable to load payroll details for editing.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setPrefetchingPayrollId(null);
    }
  };

  const handlePayrollSave = () => {
    fetchPayrolls();
    setNotification({
      type: 'success',
      message: payrollModalMode === 'edit' ? 'Payroll updated successfully.' : 'Payroll created successfully.'
    });
  };

  const handleClosePayrollModal = () => {
    setIsPayrollModalOpen(false);
    setSelectedPayroll(null);
  };

  const handleViewPayroll = async (payrollId) => {
    setViewingPayrollId(payrollId);
    setIsPayrollDetailsModalOpen(true);
    setIsPayrollDetailsLoading(true);
    setPayrollDetailsError('');
    setPayrollDetails(null);
    try {
      const detail = await loadPayrollDetails(payrollId);
      setPayrollDetails(detail);
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to fetch payroll details.';
      setPayrollDetailsError(apiError);
    } finally {
      setIsPayrollDetailsLoading(false);
      setViewingPayrollId(null);
    }
  };

  const handleClosePayrollDetails = () => {
    setIsPayrollDetailsModalOpen(false);
    setPayrollDetails(null);
    setPayrollDetailsError('');
  };

  const handleDeletePayrollRequest = (payroll) => {
    if (!payroll?.id) return;
    setPayrollPendingDelete(payroll);
  };

  const handleDeletePayrollConfirm = async () => {
    const payrollId = payrollPendingDelete?.id;
    if (!payrollId) return;

    setDeletingPayrollId(payrollId);
    try {
      await axios.delete(API_ENDPOINTS.PAYROLL_DELETE(payrollId), {
        headers: getAuthHeaders()
      });
      setNotification({ type: 'success', message: 'Payroll deleted successfully.' });
      await fetchPayrolls();
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Failed to delete payroll.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setDeletingPayrollId(null);
      setPayrollPendingDelete(null);
    }
  };

  // Attendance functions
  const fetchAttendances = async () => {
    setIsLoadingAttendances(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.ATTENDANCE_LIST, { headers });
      
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (Array.isArray(response.data.results)) {
        items = response.data.results;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      }
      
      setAttendances(items);
    } catch (error) {
      setAttendances([]);
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Unable to load attendances.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setIsLoadingAttendances(false);
    }
  };

  const loadAttendanceDetails = async (attendanceId) => {
    const headers = getAuthHeaders();
    const response = await axios.get(API_ENDPOINTS.ATTENDANCE_DETAIL(attendanceId), { headers });
    return response.data;
  };

  const handleAddAttendance = () => {
    setSelectedAttendance(null);
    setAttendanceModalMode('add');
    setIsAttendanceModalOpen(true);
  };

  const handleEditAttendance = async (attendance) => {
    if (!attendance?.id) return;
    setPrefetchingAttendanceId(attendance.id);
    try {
      const detailedAttendance = await loadAttendanceDetails(attendance.id);
      setSelectedAttendance(detailedAttendance);
      setAttendanceModalMode('edit');
      setIsAttendanceModalOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 'Unable to load attendance details for editing.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setPrefetchingAttendanceId(null);
    }
  };

  const handleAttendanceSave = () => {
    fetchAttendances();
    setNotification({
      type: 'success',
      message: attendanceModalMode === 'edit' ? 'Attendance updated successfully.' : 'Attendance created successfully.'
    });
  };

  const handleCloseAttendanceModal = () => {
    setIsAttendanceModalOpen(false);
    setSelectedAttendance(null);
  };

  const handleViewAttendance = async (attendanceId) => {
    setViewingAttendanceId(attendanceId);
    setIsAttendanceDetailsModalOpen(true);
    setIsAttendanceDetailsLoading(true);
    setAttendanceDetailsError('');
    setAttendanceDetails(null);
    try {
      const detail = await loadAttendanceDetails(attendanceId);
      setAttendanceDetails(detail);
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to fetch attendance details.';
      setAttendanceDetailsError(apiError);
    } finally {
      setIsAttendanceDetailsLoading(false);
      setViewingAttendanceId(null);
    }
  };

  const handleCloseAttendanceDetails = () => {
    setIsAttendanceDetailsModalOpen(false);
    setAttendanceDetails(null);
    setAttendanceDetailsError('');
  };

  const handleDeleteAttendanceRequest = (attendance) => {
    if (!attendance?.id) return;
    setAttendancePendingDelete(attendance);
  };

  const handleDeleteAttendanceConfirm = async () => {
    const attendanceId = attendancePendingDelete?.id;
    if (!attendanceId) return;

    setDeletingAttendanceId(attendanceId);
    try {
      await axios.delete(API_ENDPOINTS.ATTENDANCE_DELETE(attendanceId), {
        headers: getAuthHeaders()
      });
      setNotification({ type: 'success', message: 'Attendance deleted successfully.' });
      await fetchAttendances();
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Failed to delete attendance.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setDeletingAttendanceId(null);
      setAttendancePendingDelete(null);
    }
  };


  const renderPayrollTab = () => {
    const totalGrossSalary = payrolls.reduce((sum, payroll) => {
      const grossSalary = payroll?.payroll_data?.gross_salary || 0;
      return sum + Number(grossSalary);
    }, 0);
    
    const totalDeductions = payrolls.reduce((sum, payroll) => {
      const deductions = payroll?.payroll_data?.deductions || {};
      const deduction = 
        (Number(deductions.income_tax) || 0) +
        (Number(deductions.provident_fund) || 0) +
        (Number(deductions.professional_tax) || 0);
      return sum + deduction;
    }, 0);
    
    const totalNetSalary = payrolls.reduce((sum, payroll) => {
      const netSalary = payroll?.payroll_data?.net_salary || 0;
      return sum + Number(netSalary);
    }, 0);
    
    const normalizedSearch = payrollSearchTerm.trim().toLowerCase();
    const filteredPayrolls = payrolls.filter((payroll) => {
      if (!normalizedSearch) return true;
      
      const employeeName = payroll?.employee_name || '';
      const employeeId = payroll?.employee?.toString() || '';
      const status = payroll?.payroll_data?.status || '';
      
      const searchableFields = [
        employeeName,
        employeeId,
        status,
        payroll?.id?.toString()
      ];
      return searchableFields.some((field) => {
        if (!field) return false;
        return String(field).toLowerCase().includes(normalizedSearch);
      });
    });
    
    const showNoPayrolls = !isLoadingPayrolls && payrolls.length === 0;
    const showNoMatches = !isLoadingPayrolls && payrolls.length > 0 && filteredPayrolls.length === 0;

    const notificationStyles = notification?.type === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : notification?.type === 'success'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-blue-200 bg-blue-50 text-blue-800';

    const renderNotificationIcon = () => {
      if (notification?.type === 'error') {
        return <AlertCircle className="w-5 h-5 mt-0.5" />;
      }
      if (notification?.type === 'success') {
        return <CheckCircle className="w-5 h-5 mt-0.5" />;
      }
      return <Info className="w-5 h-5 mt-0.5" />;
    };
    
    return (
      <div className="space-y-6">
        {notification?.message && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${notificationStyles}`}>
            {renderNotificationIcon()}
            <p className="text-sm">{notification.message}</p>
          </div>
        )}

        {/* Payroll Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Payroll</p>
                <p className="text-2xl font-bold text-foreground">₹{totalGrossSalary.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-2">Total gross salary</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Deductions</p>
                <p className="text-2xl font-bold text-foreground">₹{totalDeductions.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <FileText className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-sm text-red-600 mt-2">Taxes & Benefits</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Net Pay</p>
                <p className="text-2xl font-bold text-foreground">₹{totalNetSalary.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-green-600 mt-2">Total net salary</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Records</p>
                <p className="text-2xl font-bold text-foreground">{payrolls.length}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-purple-600 mt-2">Payroll records</p>
          </div>
        </div>

        {/* Payroll Actions */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Payroll Management</h3>
            <Button onClick={handleAddPayroll}>
              <Plus className="w-4 h-4 mr-2" />
              Create Payroll
            </Button>
          </div>
          <div className="flex space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search payrolls..."
                  value={payrollSearchTerm}
                  onChange={(e) => setPayrollSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <Button variant="outline">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>

        {/* Payroll History */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-foreground">Payroll History</h3>
          </div>
          {isLoadingPayrolls ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Loading payrolls...</p>
              </div>
            </div>
          ) : showNoPayrolls ? (
            <div className="flex flex-col items-center justify-center py-12">
              <DollarSign className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">No payroll records yet</p>
              <p className="text-sm text-muted-foreground mb-4">Get started by creating your first payroll</p>
              <Button onClick={handleAddPayroll}>
                <Plus className="w-4 h-4 mr-2" />
                Create Payroll
              </Button>
            </div>
          ) : showNoMatches ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Search className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-lg font-medium text-gray-900 mb-1">No matching payrolls</p>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search term to find what you need.
              </p>
              <Button variant="outline" onClick={() => setPayrollSearchTerm('')}>
                Clear search
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Period</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Salary</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Salary</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayrolls.map((payroll) => (
                    <tr key={payroll.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-3">
                            <span className="text-white text-sm font-semibold">
                              {payroll?.employee_name ? payroll.employee_name.substring(0, 2).toUpperCase() : '??'}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{payroll?.employee_name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">ID: {payroll?.employee || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDisplayDate(payroll?.pay_period_start)} - {formatDisplayDate(payroll?.pay_period_end)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹{payroll?.payroll_data?.gross_salary ? Number(payroll.payroll_data.gross_salary).toLocaleString() : '0'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₹{payroll?.payroll_data?.net_salary ? Number(payroll.payroll_data.net_salary).toLocaleString() : '0'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          payroll?.payroll_data?.status === 'approved' || payroll?.payroll_data?.status === 'paid'
                            ? 'bg-green-100 text-green-800' 
                            : payroll?.payroll_data?.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {payroll?.payroll_data?.status ? payroll.payroll_data.status.charAt(0).toUpperCase() + payroll.payroll_data.status.slice(1) : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            title="View details"
                            className="inline-flex items-center gap-1 rounded-full border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleViewPayroll(payroll.id)}
                            disabled={viewingPayrollId === payroll.id}
                          >
                            {viewingPayrollId === payroll.id ? (
                              <span className="inline-block w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                            ) : (
                              <Eye className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            title="Edit payroll"
                            className="inline-flex items-center gap-1 rounded-full border border-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleEditPayroll(payroll)}
                            disabled={prefetchingPayrollId === payroll.id}
                          >
                            {prefetchingPayrollId === payroll.id ? (
                              <span className="inline-block w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin" />
                            ) : (
                              <Edit className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            title="Delete payroll"
                            className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleDeletePayrollRequest(payroll)}
                            disabled={deletingPayrollId === payroll.id}
                          >
                            {deletingPayrollId === payroll.id ? (
                              <span className="inline-block w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEmployeesTab = () => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredEmployees = employees.filter((employee) => {
      if (!normalizedSearch) return true;
      
      const empData = employee?.employee_data || {};
      // Handle both list view (simplified) and detail view (nested) structures
      const employeeName = empData?.employee_name || empData?.basic_information?.full_name || empData?.basic_information?.employee_name || '';
      const employeeId = empData?.employee_id || empData?.basic_information?.employee_id || '';
      const department = empData?.department || empData?.employment_information?.department || '';
      const designation = empData?.designation || empData?.employment_information?.designation || '';
      const officeEmail = empData?.office_email || empData?.basic_information?.office_email || '';
      const officeMobile = empData?.office_mobile_number || empData?.basic_information?.office_mobile_number || '';
      
      const searchableFields = [
        employeeName,
        employeeId,
        department,
        designation,
        officeEmail,
        officeMobile,
        employee?.id?.toString()
      ];
      return searchableFields.some((field) => {
        if (!field) return false;
        return String(field).toLowerCase().includes(normalizedSearch);
      });
    });
    
    const showNoEmployees = !isLoadingEmployees && employees.length === 0;
    const showNoMatches = !isLoadingEmployees && employees.length > 0 && filteredEmployees.length === 0;

    const notificationStyles = notification?.type === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : notification?.type === 'success'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-blue-200 bg-blue-50 text-blue-800';

    const renderNotificationIcon = () => {
      if (notification?.type === 'error') {
        return <AlertCircle className="w-5 h-5 mt-0.5" />;
      }
      if (notification?.type === 'success') {
        return <CheckCircle className="w-5 h-5 mt-0.5" />;
      }
      return <Info className="w-5 h-5 mt-0.5" />;
    };

    return (
      <div className="space-y-6">
        {notification?.message && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${notificationStyles}`}>
            {renderNotificationIcon()}
            <p className="text-sm">{notification.message}</p>
          </div>
        )}

        {/* Employee Actions */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Employee Management</h3>
            <Button onClick={handleAddEmployee}>
              <Plus className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
          </div>
          <div className="flex space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <Button variant="outline">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>

        {/* Employee Grid */}
        {isLoadingEmployees ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-sm text-muted-foreground">Loading employees...</p>
            </div>
          </div>
        ) : showNoEmployees ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">No employees yet</p>
            <p className="text-sm text-muted-foreground mb-4">Get started by adding your first employee</p>
            <Button onClick={handleAddEmployee}>
              <Plus className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
          </div>
        ) : showNoMatches ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <Search className="w-10 h-10 text-gray-400 mb-3" />
            <p className="text-lg font-medium text-gray-900 mb-1">No matching employees</p>
            <p className="text-sm text-muted-foreground mb-4">
              Try adjusting your search term to find what you need.
            </p>
            <Button variant="outline" onClick={() => setSearchTerm('')}>
              Clear search
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEmployees.map((employee) => {
              const empData = employee?.employee_data || {};
              // Handle both list view (simplified) and detail view (nested) structures
              const employeeName = empData?.employee_name || empData?.basic_information?.full_name || empData?.basic_information?.employee_name || 'N/A';
              const employeeId = empData?.employee_id || empData?.basic_information?.employee_id || '';
              const department = empData?.department || empData?.employment_information?.department || 'N/A';
              const designation = empData?.designation || empData?.employment_information?.designation || 'N/A';
              const officeEmail = empData?.office_email || empData?.basic_information?.office_email || 'N/A';
              const officeMobile = empData?.office_mobile_number || empData?.basic_information?.office_mobile_number || '';
              const personalInfo = empData?.personal_information || {};
              const employmentInfo = empData?.employment_information || {};
              const avatar = employeeName !== 'N/A' ? employeeName.substring(0, 2).toUpperCase() : '??';
              
              return (
                <div key={employee.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-xl font-semibold">{avatar}</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        title="View details"
                        aria-label="View employee details"
                        className="inline-flex items-center gap-1 rounded-full border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleViewEmployee(employee.id)}
                        disabled={viewingEmployeeId === employee.id}
                      >
                        {viewingEmployeeId === employee.id ? (
                          <span className="inline-block w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                        ) : (
                          <>
                            <Eye className="w-3.5 h-3.5" />
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        title="Edit employee"
                        aria-label="Edit employee"
                        className="inline-flex items-center gap-1 rounded-full border border-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleEditEmployee(employee)}
                        disabled={prefetchingEmployeeId === employee.id}
                      >
                        {prefetchingEmployeeId === employee.id ? (
                          <span className="inline-block w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin" />
                        ) : (
                          <>
                            <Edit className="w-3.5 h-3.5" />
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        title="Delete employee"
                        aria-label="Delete employee"
                        className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleDeleteEmployeeRequest(employee)}
                        disabled={deletingEmployeeId === employee.id}
                      >
                        {deletingEmployeeId === employee.id ? (
                          <span className="inline-block w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="w-3.5 h-3.5" />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{employeeName}</h3>
                      <p className="text-sm text-muted-foreground">{designation}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Building className="w-4 h-4 mr-2" />
                        {department}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Mail className="w-4 h-4 mr-2" />
                        {officeEmail}
                      </div>
                      {officeMobile && (
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Phone className="w-4 h-4 mr-2" />
                          {officeMobile}
                        </div>
                      )}
                      {employeeId && (
                        <div className="flex items-center text-sm text-muted-foreground">
                          <FileText className="w-4 h-4 mr-2" />
                          ID: {employeeId}
                        </div>
                      )}
                    </div>
                    
                    <div className="pt-3 border-t border-gray-200">
                      {personalInfo?.gross_salary && (
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-foreground">Gross Salary:</span>
                          <span className="text-sm font-semibold text-foreground">₹{Number(personalInfo.gross_salary).toLocaleString()}</span>
                        </div>
                      )}
                      {employmentInfo?.date_of_joining && (
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-foreground">Date of Joining:</span>
                          <span className="text-sm text-muted-foreground">{formatDisplayDate(employmentInfo.date_of_joining)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-foreground">Status:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          employee.is_active !== false
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {employee.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };


  const renderTimeTrackingTab = () => {
    const totalWorkingHours = attendances.reduce((sum, attendance) => {
      const workingHours = attendance?.attendance_data?.working_hours || 0;
      return sum + Number(workingHours);
    }, 0);
    
    const presentCount = attendances.filter(a => a?.attendance_data?.status === 'present').length;
    const absentCount = attendances.filter(a => a?.attendance_data?.status === 'absent').length;
    const lateCount = attendances.filter(a => a?.attendance_data?.status === 'late').length;
    
    const normalizedSearch = attendanceSearchTerm.trim().toLowerCase();
    const filteredAttendances = attendances.filter((attendance) => {
      if (!normalizedSearch) return true;
      
      const employeeName = attendance?.employee_name || '';
      const employeeId = attendance?.employee?.toString() || '';
      const status = attendance?.attendance_data?.status || '';
      const date = attendance?.date || '';
      
      const searchableFields = [
        employeeName,
        employeeId,
        status,
        date,
        attendance?.id?.toString()
      ];
      return searchableFields.some((field) => {
        if (!field) return false;
        return String(field).toLowerCase().includes(normalizedSearch);
      });
    });
    
    const showNoAttendances = !isLoadingAttendances && attendances.length === 0;
    const showNoMatches = !isLoadingAttendances && attendances.length > 0 && filteredAttendances.length === 0;

    const notificationStyles = notification?.type === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : notification?.type === 'success'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-blue-200 bg-blue-50 text-blue-800';

    const renderNotificationIcon = () => {
      if (notification?.type === 'error') {
        return <AlertCircle className="w-5 h-5 mt-0.5" />;
      }
      if (notification?.type === 'success') {
        return <CheckCircle className="w-5 h-5 mt-0.5" />;
      }
      return <Info className="w-5 h-5 mt-0.5" />;
    };
    
    return (
      <div className="space-y-6">
        {notification?.message && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${notificationStyles}`}>
            {renderNotificationIcon()}
            <p className="text-sm">{notification.message}</p>
          </div>
        )}

        {/* Attendance Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Working Hours</p>
                <p className="text-2xl font-bold text-foreground">{totalWorkingHours.toFixed(1)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <ClockIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-2">Total hours tracked</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Present</p>
                <p className="text-2xl font-bold text-foreground">{presentCount}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-green-600 mt-2">Present days</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Absent</p>
                <p className="text-2xl font-bold text-foreground">{absentCount}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-sm text-red-600 mt-2">Absent days</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Late</p>
                <p className="text-2xl font-bold text-foreground">{lateCount}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-sm text-orange-600 mt-2">Late arrivals</p>
          </div>
        </div>

        {/* Attendance Management */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Attendance Management</h3>
            <Button onClick={handleAddAttendance}>
              <Plus className="w-4 h-4 mr-2" />
              Create Attendance
            </Button>
          </div>
          <div className="flex space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search attendance..."
                  value={attendanceSearchTerm}
                  onChange={(e) => setAttendanceSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <Button variant="outline">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>

        {/* Attendance Table */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-foreground">Attendance Records</h3>
          </div>
          {isLoadingAttendances ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Loading attendance records...</p>
              </div>
            </div>
          ) : showNoAttendances ? (
            <div className="flex flex-col items-center justify-center py-12">
              <ClockIcon className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">No attendance records yet</p>
              <p className="text-sm text-muted-foreground mb-4">Get started by creating your first attendance record</p>
              <Button onClick={handleAddAttendance}>
                <Plus className="w-4 h-4 mr-2" />
                Create Attendance
              </Button>
            </div>
          ) : showNoMatches ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Search className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-lg font-medium text-gray-900 mb-1">No matching attendance records</p>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search term to find what you need.
              </p>
              <Button variant="outline" onClick={() => setAttendanceSearchTerm('')}>
                Clear search
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check In</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check Out</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Working Hours</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAttendances.map((attendance) => (
                    <tr key={attendance.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-3">
                            <span className="text-white text-sm font-semibold">
                              {attendance?.employee_name ? attendance.employee_name.substring(0, 2).toUpperCase() : '??'}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{attendance?.employee_name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">ID: {attendance?.employee || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDisplayDate(attendance?.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {attendance?.attendance_data?.check_in_time || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {attendance?.attendance_data?.check_out_time || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {attendance?.attendance_data?.working_hours ? `${attendance.attendance_data.working_hours} hrs` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          attendance?.attendance_data?.status === 'present'
                            ? 'bg-green-100 text-green-800' 
                            : attendance?.attendance_data?.status === 'absent'
                            ? 'bg-red-100 text-red-800'
                            : attendance?.attendance_data?.status === 'late'
                            ? 'bg-yellow-100 text-yellow-800'
                            : attendance?.attendance_data?.status === 'half_day'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {attendance?.attendance_data?.status ? attendance.attendance_data.status.charAt(0).toUpperCase() + attendance.attendance_data.status.slice(1).replace('_', ' ') : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            title="View details"
                            className="inline-flex items-center gap-1 rounded-full border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleViewAttendance(attendance.id)}
                            disabled={viewingAttendanceId === attendance.id}
                          >
                            {viewingAttendanceId === attendance.id ? (
                              <span className="inline-block w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                            ) : (
                              <Eye className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            title="Edit attendance"
                            className="inline-flex items-center gap-1 rounded-full border border-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleEditAttendance(attendance)}
                            disabled={prefetchingAttendanceId === attendance.id}
                          >
                            {prefetchingAttendanceId === attendance.id ? (
                              <span className="inline-block w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin" />
                            ) : (
                              <Edit className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            title="Delete attendance"
                            className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleDeleteAttendanceRequest(attendance)}
                            disabled={deletingAttendanceId === attendance.id}
                          >
                            {deletingAttendanceId === attendance.id ? (
                              <span className="inline-block w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-primary rounded-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-foreground">Payroll & HR Management</h1>
            </div>
            <p className="text-muted-foreground">
              Manage employee payroll, time tracking, and HR functions
            </p>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg border border-gray-200 mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6">
                {[
                  { id: 'employees', label: 'Employees', icon: Users },
                  { id: 'payroll', label: 'Payroll', icon: DollarSign },
                  { id: 'time-tracking', label: 'Time Tracking', icon: ClockIcon }
                ].map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                        activeTab === tab.id
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                      }`}
                    >
                      <IconComponent className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'payroll' && renderPayrollTab()}
              {activeTab === 'employees' && renderEmployeesTab()}
              {activeTab === 'time-tracking' && renderTimeTrackingTab()}
            </div>
          </div>
        </div>
      </div>

      {/* Employee Modals */}
      <EmployeeModal
        isOpen={isEmployeeModalOpen}
        onClose={handleCloseEmployeeModal}
        employee={selectedEmployee}
        onSave={handleEmployeeSave}
        mode={employeeModalMode}
      />
      <EmployeeDetailsModal
        isOpen={isEmployeeDetailsModalOpen}
        onClose={handleCloseEmployeeDetails}
        employee={employeeDetails}
        isLoading={isEmployeeDetailsLoading}
        error={employeeDetailsError}
      />
      {employeePendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-red-600 font-semibold">Delete Employee</p>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {(() => {
                      const empData = employeePendingDelete?.employee_data || {};
                      // Handle both list view (simplified) and detail view (nested) structures
                      return empData?.employee_name || empData?.basic_information?.full_name || empData?.basic_information?.employee_name || 'Confirm deletion';
                    })()}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action cannot be undone. The employee record and its related data will be permanently removed.
              </p>
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">Employee details</p>
                <p className="text-sm text-gray-900">
                  {(() => {
                    const empData = employeePendingDelete?.employee_data || {};
                    // Handle both list view (simplified) and detail view (nested) structures
                    return empData?.employee_name || empData?.basic_information?.full_name || empData?.basic_information?.employee_name || 'N/A';
                  })()}
                </p>
                <p className="text-xs text-muted-foreground">
                  Employee ID: {(() => {
                    const empData = employeePendingDelete?.employee_data || {};
                    return empData?.employee_id || empData?.basic_information?.employee_id || 'N/A';
                  })()}
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={deletingEmployeeId === employeePendingDelete?.id}
                  onClick={() => setEmployeePendingDelete(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deletingEmployeeId === employeePendingDelete?.id}
                  onClick={handleDeleteEmployeeConfirm}
                >
                  {deletingEmployeeId === employeePendingDelete?.id ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payroll Modals */}
      <PayrollModal
        isOpen={isPayrollModalOpen}
        onClose={handleClosePayrollModal}
        payroll={selectedPayroll}
        employees={employees}
        onSave={handlePayrollSave}
        mode={payrollModalMode}
      />
      <PayrollDetailsModal
        isOpen={isPayrollDetailsModalOpen}
        onClose={handleClosePayrollDetails}
        payroll={payrollDetails}
        isLoading={isPayrollDetailsLoading}
        error={payrollDetailsError}
      />
      {payrollPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-red-600 font-semibold">Delete Payroll</p>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {payrollPendingDelete?.employee_name || 'Confirm deletion'}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action cannot be undone. The payroll record and its related data will be permanently removed.
              </p>
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">Payroll details</p>
                <p className="text-sm text-gray-900">
                  {payrollPendingDelete?.employee_name || 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Period: {formatDisplayDate(payrollPendingDelete?.pay_period_start)} - {formatDisplayDate(payrollPendingDelete?.pay_period_end)}
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={deletingPayrollId === payrollPendingDelete?.id}
                  onClick={() => setPayrollPendingDelete(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deletingPayrollId === payrollPendingDelete?.id}
                  onClick={handleDeletePayrollConfirm}
                >
                  {deletingPayrollId === payrollPendingDelete?.id ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Modals */}
      <AttendanceModal
        isOpen={isAttendanceModalOpen}
        onClose={handleCloseAttendanceModal}
        attendance={selectedAttendance}
        employees={employees}
        onSave={handleAttendanceSave}
        mode={attendanceModalMode}
      />
      <AttendanceDetailsModal
        isOpen={isAttendanceDetailsModalOpen}
        onClose={handleCloseAttendanceDetails}
        attendance={attendanceDetails}
        isLoading={isAttendanceDetailsLoading}
        error={attendanceDetailsError}
      />
      {attendancePendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-red-600 font-semibold">Delete Attendance</p>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {attendancePendingDelete?.employee_name || 'Confirm deletion'}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action cannot be undone. The attendance record and its related data will be permanently removed.
              </p>
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">Attendance details</p>
                <p className="text-sm text-gray-900">
                  {attendancePendingDelete?.employee_name || 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Date: {formatDisplayDate(attendancePendingDelete?.date)}
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={deletingAttendanceId === attendancePendingDelete?.id}
                  onClick={() => setAttendancePendingDelete(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deletingAttendanceId === attendancePendingDelete?.id}
                  onClick={handleDeleteAttendanceConfirm}
                >
                  {deletingAttendanceId === attendancePendingDelete?.id ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollHR; 